import {
  type PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import type { ServiceRequest } from '../../types/shared';
import type { RootState } from '../index';
import {
  createRequest,
  deleteRequest,
  fetchRequestById,
  fetchRequests,
  updateRequest,
} from '../thunks/requestThunks';

// Entity adapter for normalized state
const requestsAdapter = createEntityAdapter<ServiceRequest>({
  selectId: (request) => request.id,
  sortComparer: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
});

// Re-export thunks for convenience
export { createRequest, deleteRequest, fetchRequestById, fetchRequests, updateRequest };

// Slice
const requestsSlice = createSlice({
  name: 'requests',
  initialState: requestsAdapter.getInitialState({
    loading: 'idle' as 'idle' | 'pending' | 'succeeded' | 'failed',
    error: null as string | null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      hasMore: false,
    },
    filters: {
      status: 'all' as 'all' | 'pending' | 'in_progress' | 'completed',
      priority: 'all' as 'all' | 'low' | 'medium' | 'high' | 'urgent',
      category: 'all',
      dateRange: null as { start: string; end: string } | null,
    },
  }),
  reducers: {
    setFilter: (state, action: PayloadAction<Partial<typeof state.filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        priority: 'all',
        category: 'all',
        dateRange: null,
      };
      state.pagination.page = 1;
    },
    requestAdded: requestsAdapter.addOne,
    requestUpdated: requestsAdapter.updateOne,
    requestRemoved: requestsAdapter.removeOne,
  },
  extraReducers: (builder) => {
    builder
      // Fetch requests
      .addCase(fetchRequests.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        requestsAdapter.setAll(state, action.payload);
        state.pagination.total = action.payload.length;
        state.pagination.hasMore = action.payload.length >= state.pagination.limit;
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.error.message || 'Failed to fetch requests';
      })
      // Create request
      .addCase(createRequest.fulfilled, (state, action) => {
        requestsAdapter.addOne(state, action.payload);
        state.pagination.total += 1;
      })
      // Update request
      .addCase(updateRequest.fulfilled, (state, action) => {
        requestsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: action.payload,
        });
      })
      // Delete request
      .addCase(deleteRequest.fulfilled, (state, action) => {
        requestsAdapter.removeOne(state, action.payload);
        state.pagination.total -= 1;
      });
  },
});

// Export actions
export const { setFilter, clearFilters, requestAdded, requestUpdated, requestRemoved } =
  requestsSlice.actions;

// Export homeownerActions for backward compatibility
export const homeownerActions = {
  addMyRequest: requestAdded,
};

// Export selectors
export const {
  selectAll: selectAllRequests,
  selectById: selectRequestById,
  selectIds: selectRequestIds,
  selectEntities: selectRequestEntities,
  selectTotal: selectTotalRequests,
} = requestsAdapter.getSelectors((state: RootState) => state.requests);

// Custom selectors with memoization

export const selectRequestsLoading = (state: RootState) => state.requests.loading;
export const selectRequestsError = (state: RootState) => state.requests.error;
export const selectRequestsFilters = (state: RootState) => state.requests.filters;
export const selectRequestsPagination = (state: RootState) => state.requests.pagination;

// Filtered requests selector
export const selectFilteredRequests = createSelector(
  [selectAllRequests, selectRequestsFilters],
  (requests, filters) => {
    let filtered = requests;

    if (filters.status !== 'all') {
      filtered = filtered.filter((r) => r.status === filters.status);
    }

    if (filters.priority !== 'all') {
      filtered = filtered.filter((r) => r.priority === filters.priority);
    }

    if (filters.category !== 'all') {
      filtered = filtered.filter((r) => r.category === filters.category);
    }

    if (filters.dateRange) {
      filtered = filtered.filter((r) => {
        const createdAt = new Date(r.createdAt).toISOString();
        return createdAt >= filters.dateRange?.start && createdAt <= filters.dateRange?.end;
      });
    }

    return filtered;
  }
);

// Stats selector
export const selectRequestStats = createSelector([selectAllRequests], (requests) => ({
  total: requests.length,
  pending: requests.filter((r) => r.status === 'pending').length,
  inProgress: requests.filter((r) => r.status === 'in_progress').length,
  completed: requests.filter((r) => r.status === 'completed').length,
  urgent: requests.filter((r) => r.priority === 'urgent').length,
}));

export default requestsSlice.reducer;
